import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  Divider,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Chip,
  Stack,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import InfoIcon from "@mui/icons-material/Info";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import {
  fetchUserBalance,
  fetchPolBalance,
  swapTokens,
  resetTransactionState,
} from "../../redux/ndeipiCoinSlice";
import { formatBalance } from "../../utils/balanceUtils";

interface SwapSectionProps {
  isMobile: boolean;
}

interface TokenOption {
  symbol: string;
  name: string;
  balance: number;
}

const SwapSection: React.FC<SwapSectionProps> = ({ isMobile }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  // Redux selectors
  const { userBalance, polBalance, currentTransaction } = useAppSelector(
    (state) => state.ndeipiCoin
  );
  const { chainType } = useAppSelector((state) => state.wallet);
  const { assetBalances } = useAppSelector((state) => state.assets);

  // State
  const [fromAmount, setFromAmount] = useState<string>("");
  const [toAmount, setToAmount] = useState<string>("");
  const [slippage, setSlippage] = useState<number>(0.5); // 0.5% default slippage
  const [selectedFromToken, setSelectedFromToken] = useState<string>("GNM");
  const [selectedToToken, setSelectedToToken] = useState<string>("NDEIPI");
  const [showTokenSelector, setShowTokenSelector] = useState<
    "from" | "to" | null
  >(null);
  const [isSwapping, setIsSwapping] = useState<boolean>(false);
  const [swapRate, setSwapRate] = useState<number>(1);
  const [transactionFee, setTransactionFee] = useState<number>(0.01);

  // Available tokens (including Ndeipi and assets from Redux)
  const [availableTokens, setAvailableTokens] = useState<TokenOption[]>([
    {
      symbol: "NDEIPI",
      name: "Ndeipi Token",
      balance: parseFloat(userBalance || "0"),
    },
    // { symbol: "GNM", name: "USD Balance", balance: 1000 }, // Mock GNM balance
  ]);

  // Fetch balances on mount
  useEffect(() => {
    if (chainType === "evm") {
      dispatch(fetchUserBalance());
      dispatch(fetchPolBalance());
    }
  }, [dispatch, chainType]);

  // Update Ndeipi balance when userBalance changes
  useEffect(() => {
    setAvailableTokens((prev) =>
      prev.map((token) =>
        token.symbol === "NDEIPI"
          ? { ...token, balance: parseFloat(userBalance || "0") }
          : token
      )
    );
  }, [userBalance]);

  // Add assets from Redux to available tokens
  useEffect(() => {
    if (assetBalances && assetBalances.length > 0) {
      const assetTokens = assetBalances.map((asset) => ({
        symbol: asset.asset.symbol,
        name: asset.asset.name,
        balance: asset.balance,
      }));

      // Filter out duplicates
      const existingSymbols = new Set(availableTokens.map((t) => t.symbol));
      const newTokens = assetTokens.filter(
        (token) => !existingSymbols.has(token.symbol)
      );

      setAvailableTokens((prev) => [...prev, ...newTokens]);
    }
  }, [assetBalances]);

  // Calculate swap rate (mock calculation - replace with actual API call)
  useEffect(() => {
    // Mock swap rate calculation
    // In a real app, you would fetch this from a price oracle or DEX
    if (selectedFromToken === "NDEIPI" && selectedToToken === "GNM") {
      setSwapRate(0.05); // 1 NDEIPI = 0.05 GNM
    } else if (selectedFromToken === "GNM" && selectedToToken === "NDEIPI") {
      setSwapRate(20); // 1 GNM = 20 NDEIPI
    } else {
      setSwapRate(1); // Default 1:1 for other tokens
    }
  }, [selectedFromToken, selectedToToken]);

  // Calculate toAmount when fromAmount changes
  useEffect(() => {
    if (fromAmount && !isNaN(parseFloat(fromAmount))) {
      const calculatedAmount = parseFloat(fromAmount) * swapRate;
      setToAmount(calculatedAmount.toFixed(6));
    } else {
      setToAmount("");
    }
  }, [fromAmount, swapRate]);

  // Get selected token balance
  const getTokenBalance = (tokenSymbol: string): number => {
    const token = availableTokens.find((t) => t.symbol === tokenSymbol);
    return token ? token.balance : 0;
  };

  // Handle token selection
  const handleTokenSelect = (tokenSymbol: string, type: "from" | "to") => {
    if (type === "from") {
      setSelectedFromToken(tokenSymbol);
      // Ensure from and to tokens are not the same
      if (tokenSymbol === selectedToToken) {
        const otherToken = availableTokens.find(
          (t) => t.symbol !== tokenSymbol
        );
        if (otherToken) {
          setSelectedToToken(otherToken.symbol);
        }
      }
    } else {
      setSelectedToToken(tokenSymbol);
      // Ensure from and to tokens are not the same
      if (tokenSymbol === selectedFromToken) {
        const otherToken = availableTokens.find(
          (t) => t.symbol !== tokenSymbol
        );
        if (otherToken) {
          setSelectedFromToken(otherToken.symbol);
        }
      }
    }
    setShowTokenSelector(null);
  };

  // Handle max amount
  const handleMaxAmount = () => {
    const balance = getTokenBalance(selectedFromToken);
    setFromAmount(balance.toString());
  };

  // Handle swap
  const handleSwap = async () => {
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      return;
    }

    const fromBalance = getTokenBalance(selectedFromToken);
    if (parseFloat(fromAmount) > fromBalance) {
      alert("Insufficient balance");
      return;
    }

    setIsSwapping(true);

    try {
      // Dispatch swap action
      await dispatch(
        swapTokens({
          fromToken: selectedFromToken,
          toToken: selectedToToken,
          amount: fromAmount,
          slippage: slippage.toString(),
        })
      ).unwrap();

      // Refresh balances after successful swap
      dispatch(fetchUserBalance());
      dispatch(fetchPolBalance());

      // Reset form
      setFromAmount("");
      setToAmount("");
    } catch (error) {
      console.error("Swap failed:", error);
    } finally {
      setIsSwapping(false);
    }
  };

  // Handle swap direction reversal
  const handleReverseSwap = () => {
    setSelectedFromToken(selectedToToken);
    setSelectedToToken(selectedFromToken);
    setFromAmount(toAmount);
  };

  // Render token selector dropdown
  const renderTokenSelector = (type: "from" | "to") => {
    const selectedToken = type === "from" ? selectedFromToken : selectedToToken;

    return (
      <Paper
        elevation={3}
        sx={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          zIndex: 1000,
          maxHeight: 300,
          overflow: "auto",
          mt: 1,
        }}
      >
        {availableTokens.map((token) => (
          <Box
            key={token.symbol}
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
            onClick={() => handleTokenSelect(token.symbol, type)}
          >
            <Box>
              <Typography variant="body1" fontWeight="bold">
                {token.symbol}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {token.name}
              </Typography>
            </Box>
            <Typography variant="body2">
              {formatBalance(token.balance.toString())}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  };

  // Get token icon (placeholder function)
  const getTokenIcon = (symbol: string) => {
    return symbol.charAt(0);
  };

  const scrollableBoxStyles = {
    maxHeight: { xs: isMobile ? "80vh" : "68vh", sm: "68vh" },
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3, ...scrollableBoxStyles }}>
      {/* Header */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: "bold",
          mb: 3,
          fontSize: isMobile ? "1.5rem" : "2rem",
          textAlign: "center",
        }}
      >
        Swap Tokens
      </Typography>

      {/* Swap Card */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 3,
          //   background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
        }}
      >
        {/* From Token Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              From
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Balance:{" "}
              {formatBalance(getTokenBalance(selectedFromToken).toString())}
            </Typography>
          </Box>

          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 1.5,
                backgroundColor: "background.paper",
                cursor: "pointer",
              }}
              onClick={() =>
                setShowTokenSelector(
                  showTokenSelector === "from" ? null : "from"
                )
              }
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "bold",
                  mr: 2,
                }}
              >
                {getTokenIcon(selectedFromToken)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedFromToken}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {
                    availableTokens.find((t) => t.symbol === selectedFromToken)
                      ?.name
                  }
                </Typography>
              </Box>
              {showTokenSelector === "from" ? (
                <ArrowDropUpIcon />
              ) : (
                <ArrowDropDownIcon />
              )}
            </Box>

            {showTokenSelector === "from" && renderTokenSelector("from")}
          </Box>

          <TextField
            fullWidth
            type="number"
            variant="outlined"
            placeholder="0.0"
            value={fromAmount}
            onChange={(e) => setFromAmount(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={handleMaxAmount}
                    sx={{ textTransform: "none" }}
                  >
                    MAX
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 2 }}
          />
        </Box>

        {/* Swap Button (Middle) */}
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <IconButton
            onClick={handleReverseSwap}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "white",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
              width: 48,
              height: 48,
            }}
          >
            <SwapHorizIcon />
          </IconButton>
        </Box>

        {/* To Token Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              To
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Balance:{" "}
              {formatBalance(getTokenBalance(selectedToToken).toString())}
            </Typography>
          </Box>

          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 1.5,
                backgroundColor: "background.paper",
                cursor: "pointer",
              }}
              onClick={() =>
                setShowTokenSelector(showTokenSelector === "to" ? null : "to")
              }
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.secondary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "bold",
                  mr: 2,
                }}
              >
                {getTokenIcon(selectedToToken)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedToToken}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {
                    availableTokens.find((t) => t.symbol === selectedToToken)
                      ?.name
                  }
                </Typography>
              </Box>
              {showTokenSelector === "to" ? (
                <ArrowDropUpIcon />
              ) : (
                <ArrowDropDownIcon />
              )}
            </Box>

            {showTokenSelector === "to" && renderTokenSelector("to")}
          </Box>

          <TextField
            fullWidth
            type="number"
            variant="outlined"
            placeholder="0.0"
            value={toAmount}
            disabled
            sx={{ mt: 2 }}
          />
        </Box>

        {/* Swap Details */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Swap Details
          </Typography>

          <Stack spacing={1}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Rate</Typography>
              <Typography variant="body2" fontWeight="bold">
                1 {selectedFromToken} = {swapRate.toFixed(6)} {selectedToToken}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Slippage Tolerance</Typography>
              <Typography variant="body2" fontWeight="bold">
                {slippage}%
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Estimated Fee</Typography>
              <Typography variant="body2" fontWeight="bold">
                ~{transactionFee} {selectedFromToken}
              </Typography>
            </Box>
          </Stack>

          {/* Slippage Settings */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Slippage Tolerance
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <Slider
                value={slippage}
                onChange={(_, value) => setSlippage(value as number)}
                min={0.1}
                max={5}
                step={0.1}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                value={slippage}
                onChange={(e) => setSlippage(parseFloat(e.target.value) || 0.5)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                sx={{ width: 80 }}
              />
            </Box>
          </Box>
        </Box>

        {/* Swap Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSwap}
          disabled={!fromAmount || parseFloat(fromAmount) <= 0 || isSwapping}
          sx={{
            mt: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1.1rem",
            fontWeight: "bold",
          }}
        >
          {isSwapping ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              Swapping...
            </>
          ) : (
            `Swap ${selectedFromToken} for ${selectedToToken}`
          )}
        </Button>

        {/* Transaction Status */}
        {currentTransaction.status && (
          <Box sx={{ mt: 3 }}>
            {currentTransaction.status === "pending" && (
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                Transaction in progress...
              </Alert>
            )}
            {currentTransaction.status === "success" && (
              <Alert
                severity="success"
                onClose={() => dispatch(resetTransactionState())}
              >
                Swap completed successfully!
              </Alert>
            )}
            {currentTransaction.status === "failed" && (
              <Alert
                severity="error"
                onClose={() => dispatch(resetTransactionState())}
              >
                Swap failed: {currentTransaction.error}
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Info Section */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          backgroundColor: theme.palette.primary.light,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <InfoIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="subtitle2" color={"text.secondary"}>
            Important Information
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          • All swaps are executed on-chain and may take a few minutes to
          complete
          <br />
          • Transaction fees apply and will be deducted from your balance
          <br />
          • Ensure you have sufficient POL for gas fees and commissions
          <br />• Slippage tolerance helps prevent failed transactions during
          price volatility
        </Typography>
      </Box>
    </Box>
  );
};

export default SwapSection;
